import { useMemo } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import DropdownSelect from "../../../ui/dropdowns/DropdownSelect";
import styles from "../../../styles/components/Sections.module.css";

const NO_REMINDER_OPTION_ID = "none";

const REMINDER_DAY_CHOICES = [1, 2, 3, 5, 7, 14, 30];

const buildReminderDaysOptions = () => [
  { id: NO_REMINDER_OPTION_ID, name: "Sin recordatorio" },
  ...REMINDER_DAY_CHOICES.map((days) => ({
    id: String(days),
    name: days === 1 ? "1 día" : `${days} días`,
  })),
];

const toReminderOptionId = (days) =>
  days ? String(days) : NO_REMINDER_OPTION_ID;

const toReminderDays = (optionId) =>
  optionId === NO_REMINDER_OPTION_ID ? null : Number(optionId);

const PreferencesSection = () => {
  const { auth, updateThemePreference, updateReminderPreferences } = useAuth();

  const currentTheme = auth?.user?.theme_preference || "system";
  const currentFollowUpReminderDays = auth?.user?.follow_up_reminder_days ?? null;
  const currentUnansweredReminderDays =
    auth?.user?.unanswered_mail_reminder_days ?? null;

  const themeOptions = useMemo(
    () => [
      { id: "system", name: "Misma que el sistema" },
      { id: "light", name: "Claro" },
      { id: "dark", name: "Oscuro" },
    ],
    [],
  );

  const reminderDaysOptions = useMemo(buildReminderDaysOptions, []);

  const selectedTheme = useMemo(() => {
    return themeOptions.find((item) => item.id === currentTheme) || themeOptions[0];
  }, [currentTheme, themeOptions]);

  const selectedFollowUpReminder = useMemo(() => {
    const optionId = toReminderOptionId(currentFollowUpReminderDays);
    return (
      reminderDaysOptions.find((item) => item.id === optionId) ||
      reminderDaysOptions[0]
    );
  }, [currentFollowUpReminderDays, reminderDaysOptions]);

  const selectedUnansweredReminder = useMemo(() => {
    const optionId = toReminderOptionId(currentUnansweredReminderDays);
    return (
      reminderDaysOptions.find((item) => item.id === optionId) ||
      reminderDaysOptions[0]
    );
  }, [currentUnansweredReminderDays, reminderDaysOptions]);

  const handleThemeChange = async (item) => {
    if (!item?.id) return;

    try {
      await updateThemePreference(item.id);
    } catch (error) {
      console.error("Error al actualizar tema:", error);
    }
  };

  const handleFollowUpReminderChange = async (item) => {
    if (!item?.id) return;

    try {
      await updateReminderPreferences({
        follow_up_reminder_days: toReminderDays(item.id),
      });
    } catch (error) {
      console.error("Error al actualizar el recordatorio al enviar:", error);
    }
  };

  const handleUnansweredReminderChange = async (item) => {
    if (!item?.id) return;

    try {
      await updateReminderPreferences({
        unanswered_mail_reminder_days: toReminderDays(item.id),
      });
    } catch (error) {
      console.error(
        "Error al actualizar el recordatorio de correo sin responder:",
        error,
      );
    }
  };

  return (
    <div className={styles.sectionContent}>
      <div className={styles.block}>
        <div className={styles.blockTitle}>Ajustes</div>

        <div className={styles.dropdownRow}>
          <DropdownSelect
            title="Apariencia"
            items={themeOptions}
            selectedItem={selectedTheme}
            onChange={handleThemeChange}
            labelKey="name"
            isEditing={true}
          />
        </div>
      </div>

      <div className={styles.block}>
        <div className={styles.blockTitle}>Recordatorios de seguimiento</div>

        <div className={styles.dropdownRow}>
          <DropdownSelect
            title="Recordatorio al enviar un correo"
            items={reminderDaysOptions}
            selectedItem={selectedFollowUpReminder}
            onChange={handleFollowUpReminderChange}
            labelKey="name"
            isEditing={true}
          />
        </div>

        <div className={styles.dropdownRow}>
          <DropdownSelect
            title="Recordatorio por correo sin responder"
            items={reminderDaysOptions}
            selectedItem={selectedUnansweredReminder}
            onChange={handleUnansweredReminderChange}
            labelKey="name"
            isEditing={true}
          />
        </div>
      </div>
    </div>
  );
};

export default PreferencesSection;
